/* eslint-disable no-var */

/**
 * @author Eberhard Graether / http://egraether.com/
 * @author Mark Lundin 	/ http://mark-lundin.com
 * @author Patrick Fuller / http://patrick-fuller.com
 * @author Max Smolens / https://github.com/msmolens
 */
import { EventDispatcher, Quaternion, Vector2, Vector3 } from 'three';

/**
 * @type Class
 */
const OrthographicTrackballControls = function (
  object,
  domElement,
  onZoomChange,
) {
  var _this = this;
  var STATE = {
    NONE: -1,
    ROTATE: 0,
    ZOOM: 1,
    PAN: 2,
    TOUCH_ROTATE: 3,
    TOUCH_ZOOM_PAN: 4,
  };

  this.object = object;
  this.domElement = domElement !== undefined ? domElement : document;
  this.onZoomChange = onZoomChange;

  // API

  this.enabled = true;

  this.screen = { left: 0, top: 0, width: 0, height: 0 };
  this.radius = 0;

  this.rotateSpeed = 1.0;
  this.zoomSpeed = 1.2;

  this.noRotate = false;
  this.noZoom = false;
  this.noPan = false;
  this.noRoll = false;

  this.staticMoving = false;
  this.dynamicDampingFactor = 0.2;

  this.keys = [65 /*A*/, 83 /*S*/, 68 /*D*/];

  // internals

  this.target = new Vector3();

  var EPS = 0.000001;

  var _changed = true;

  var _state = STATE.NONE,
    _prevState = STATE.NONE,
    _eye = new Vector3(),
    _rotateStart = new Vector3(),
    _rotateEnd = new Vector3(),
    _zoomStart = new Vector2(),
    _zoomEnd = new Vector2(),
    _touchZoomDistanceStart = 0,
    _touchZoomDistanceEnd = 0,
    _panStart = new Vector2(),
    _panEnd = new Vector2();

  // for reset

  this.target0 = this.target.clone();
  this.position0 = this.object.position.clone();
  this.up0 = this.object.up.clone();

  this.left0 = this.object.left;
  this.right0 = this.object.right;
  this.top0 = this.object.top;
  this.bottom0 = this.object.bottom;

  // events

  var changeEvent = { type: 'change' };
  var startEvent = { type: 'start' };
  var endEvent = { type: 'end' };

  // methods

  //Note: Prior to 3/3/2023 (before https://paperlessparts.atlassian.net/browse/PP-14322 PR)
  //* Paperless change this.handleResize(). Original is below.
  //* But it introduced bugs because the passed in newOffsetLeft, newOffsetTop were not enough
  //  to properly update properties such as this.screen when in digiquote.  It is better to inspect the domElement directly
  //  for the size... and to use window.pageXOffset and window.pageYOffset to correctly account for the screen location; particularly for digiquote
  //  where the element is inside a region that is scrolling
  //* The original implementation from https://raw.githubusercontent.com/mrdoob/three.js/7e0a78beb9317e580d7fa4da9b5b12be051c6feb/examples/js/controls/OrthographicTrackballControls.js
  //  is now used below, and it resolved bug in https://paperlessparts.atlassian.net/browse/PP-14322

  // this.handleResize = function (newOffsetLeft, newOffsetTop) {
  //   this.screen.left = newOffsetLeft;
  //   this.screen.top = newOffsetTop;
  //   if (this.screen.width < window.outerWidth) {
  //     this.screen.width = window.outerWidth;
  //   }
  //   if (this.screen.height < window.outerHeight) {
  //     this.screen.height = window.outerHeight;
  //   }

  //   this.radius = 0.5 * Math.max(this.screen.width, this.screen.height);

  //   this.left0 = this.object.left;
  //   this.right0 = this.object.right;
  //   this.top0 = this.object.top;
  //   this.bottom0 = this.object.bottom;
  // };

  this.handleResize = function () {
    if (this.domElement === document) {
      this.screen.left = 0;
      this.screen.top = 0;
      this.screen.width = window.innerWidth;
      this.screen.height = window.innerHeight;
    } else {
      var box = this.domElement.getBoundingClientRect();
      // adjustments come from similar code in the jquery offset() function
      var d = this.domElement.ownerDocument.documentElement;
      this.screen.left = box.left + window.pageXOffset - d.clientLeft;
      this.screen.top = box.top + window.pageYOffset - d.clientTop;
      this.screen.width = box.width;
      this.screen.height = box.height;
    }

    //Note: The original code from threejs was basing the radius on the min of width/height
    //But it should be on the max.  The oribit control's ball should be around the largest dimension (width/height).
    //If we used the min, then there is a strange clipping effect when rotating by sliding the mouse along the long dimension
    this.radius = 0.5 * Math.max(this.screen.width, this.screen.height);

    this.left0 = this.object.left;
    this.right0 = this.object.right;
    this.top0 = this.object.top;
    this.bottom0 = this.object.bottom;
  };

  var getMouseOnScreen = (function () {
    var vector = new Vector2();

    return function getMouseOnScreen(pageX, pageY) {
      vector.set(
        (pageX - _this.screen.left) / _this.screen.width,
        (pageY - _this.screen.top) / _this.screen.height,
      );

      return vector;
    };
  })();

  var getMouseProjectionOnBall = (function () {
    var vector = new Vector3();
    var objectUp = new Vector3();
    var mouseOnBall = new Vector3();

    return function getMouseProjectionOnBall(pageX, pageY) {
      mouseOnBall.set(
        (pageX - _this.screen.width * 0.5 - _this.screen.left) / _this.radius,
        (_this.screen.height * 0.5 + _this.screen.top - pageY) / _this.radius,
        0.0,
      );
      var length = mouseOnBall.length();

      if (_this.noRoll) {
        if (length < Math.SQRT1_2) {
          mouseOnBall.z = Math.sqrt(1.0 - length * length);
        } else {
          mouseOnBall.z = 0.5 / length;
        }
      } else if (length > 1.0) {
        mouseOnBall.normalize();
      } else {
        mouseOnBall.z = Math.sqrt(1.0 - length * length);
      }

      _eye.copy(_this.object.position).sub(_this.target);

      vector.copy(_this.object.up).setLength(mouseOnBall.y);
      vector.add(
        objectUp.copy(_this.object.up).cross(_eye).setLength(mouseOnBall.x),
      );
      vector.add(_eye.setLength(mouseOnBall.z));

      return vector;
    };
  })();

  this.rotateCamera = (function () {
    var axis = new Vector3(),
      quaternion = new Quaternion();

    return function rotateCamera() {
      //Paperless changed var to let (should make no functional diff)
      let angle = Math.acos(
        _rotateStart.dot(_rotateEnd) /
          _rotateStart.length() /
          _rotateEnd.length(),
      );

      if (angle) {
        axis.crossVectors(_rotateStart, _rotateEnd).normalize();

        angle *= _this.rotateSpeed;

        //Paperless addition: (clamp angle so we rotate no more than 45 degrees)
        //Note sure what history was that lead to this need... but I am okay with it. I doubt there are many
        //cases where a mouse movement would rotate greater... (perhaps this was important before the rendering performance improvements occurred)
        angle = Math.min(angle, Math.PI / 4);

        quaternion.setFromAxisAngle(axis, -angle);

        _eye.applyQuaternion(quaternion);
        _this.object.up.applyQuaternion(quaternion);

        _rotateEnd.applyQuaternion(quaternion);

        if (_this.staticMoving) {
          _rotateStart.copy(_rotateEnd);
        } else {
          quaternion.setFromAxisAngle(
            axis,
            angle * (_this.dynamicDampingFactor - 1.0),
          );
          _rotateStart.applyQuaternion(quaternion);
        }

        _changed = true;
      }
    };
  })();

  this.zoomCamera = function () {
    let factor;
    if (_state === STATE.TOUCH_ZOOM_PAN) {
      factor = _touchZoomDistanceEnd / _touchZoomDistanceStart;
      _touchZoomDistanceStart = _touchZoomDistanceEnd;

      _this.object.zoom *= factor;

      _changed = true;
    } else {
      factor = 1.0 + (_zoomEnd.y - _zoomStart.y) * _this.zoomSpeed;

      if (Math.abs(factor - 1.0) > EPS && factor > 0.0) {
        _this.object.zoom /= factor;

        if (_this.staticMoving) {
          _zoomStart.copy(_zoomEnd);
        } else {
          _zoomStart.y +=
            (_zoomEnd.y - _zoomStart.y) * this.dynamicDampingFactor;
        }

        _changed = true;
      }
    }
    //Paperless addition: calls the registered callback with zoom state
    this.onZoomChange(_this.object.zoom);
  };

  this.panCamera = (function () {
    var mouseChange = new Vector2(),
      objectUp = new Vector3(),
      pan = new Vector3();

    return function panCamera() {
      mouseChange.copy(_panEnd).sub(_panStart);

      if (mouseChange.lengthSq() > EPS) {
        // Scale movement to keep clicked/dragged position under cursor
        var scaleX =
          (_this.object.right - _this.object.left) / _this.object.zoom;
        var scaleY =
          (_this.object.top - _this.object.bottom) / _this.object.zoom;
        mouseChange.x *= scaleX;
        mouseChange.y *= scaleY;

        pan.copy(_eye).cross(_this.object.up).setLength(mouseChange.x);
        pan.add(objectUp.copy(_this.object.up).setLength(mouseChange.y));

        _this.object.position.add(pan);
        _this.target.add(pan);

        if (_this.staticMoving) {
          _panStart.copy(_panEnd);
        } else {
          _panStart.add(
            mouseChange
              .subVectors(_panEnd, _panStart)
              .multiplyScalar(_this.dynamicDampingFactor),
          );
        }

        _changed = true;
      }
    };
  })();

  this.update = function () {
    _eye.subVectors(_this.object.position, _this.target);

    if (!_this.noRotate) {
      _this.rotateCamera();
    }

    if (!_this.noZoom) {
      _this.zoomCamera();

      if (_changed) {
        _this.object.updateProjectionMatrix();
      }
    }

    if (!_this.noPan) {
      _this.panCamera();
    }

    _this.object.position.addVectors(_this.target, _eye);

    _this.object.lookAt(_this.target);
    if (_changed) {
      _this.dispatchEvent(changeEvent);

      _changed = false;
    }
  };

  this.reset = function () {
    _state = STATE.NONE;
    _prevState = STATE.NONE;

    _this.target.copy(_this.target0);
    _this.object.position.copy(_this.position0);
    _this.object.up.copy(_this.up0);

    _eye.subVectors(_this.object.position, _this.target);

    _this.object.left = _this.left0;
    _this.object.right = _this.right0;
    _this.object.top = _this.top0;
    _this.object.bottom = _this.bottom0;

    _this.object.lookAt(_this.target);

    _this.dispatchEvent(changeEvent);

    _changed = false;
  };

  // listeners

  function keydown(event) {
    if (_this.enabled === false) return;

    window.removeEventListener('keydown', keydown);

    _prevState = _state;

    if (_state !== STATE.NONE) {
      return;
    } else if (event.keyCode === _this.keys[STATE.ROTATE] && !_this.noRotate) {
      _state = STATE.ROTATE;
    } else if (event.keyCode === _this.keys[STATE.ZOOM] && !_this.noZoom) {
      _state = STATE.ZOOM;
    } else if (event.keyCode === _this.keys[STATE.PAN] && !_this.noPan) {
      _state = STATE.PAN;
    }
  }

  function keyup() {
    if (_this.enabled === false) return;

    _state = _prevState;

    window.addEventListener('keydown', keydown, false);
  }

  function mousedown(event) {
    if (_this.enabled === false) return;

    event.preventDefault();
    event.stopPropagation();

    if (_state === STATE.NONE) {
      _state = event.button;
    }

    if (_state === STATE.ROTATE && !_this.noRotate) {
      _rotateStart.copy(getMouseProjectionOnBall(event.pageX, event.pageY));
      _rotateEnd.copy(_rotateStart);
    } else if (_state === STATE.ZOOM && !_this.noZoom) {
      _zoomStart.copy(getMouseOnScreen(event.pageX, event.pageY));
      _zoomEnd.copy(_zoomStart);
    } else if (_state === STATE.PAN && !_this.noPan) {
      _panStart.copy(getMouseOnScreen(event.pageX, event.pageY));
      _panEnd.copy(_panStart);
    }

    document.addEventListener('mousemove', mousemove, false);
    document.addEventListener('mouseup', mouseup, false);

    _this.dispatchEvent(startEvent);
  }

  function mousemove(event) {
    if (_this.enabled === false) return;

    event.preventDefault();
    event.stopPropagation();

    if (_state === STATE.ROTATE && !_this.noRotate) {
      _rotateEnd.copy(getMouseProjectionOnBall(event.pageX, event.pageY));
    } else if (_state === STATE.ZOOM && !_this.noZoom) {
      _zoomEnd.copy(getMouseOnScreen(event.pageX, event.pageY));
    } else if (_state === STATE.PAN && !_this.noPan) {
      _panEnd.copy(getMouseOnScreen(event.pageX, event.pageY));
    }
    //Paperless addition:
    //* This was added in PP-12220
    //* Note in renderer.ts we have `this.trackball.addEventListener('change', this.requestRenderIfNotRequested as () => void)`
    //  which is necessary because "Without this addition, the render loop continuity breaks and the orbit controls freeze up."
    //* Could have been added to help with debugging in the past?
    _this.dispatchEvent(changeEvent);
  }

  function mouseup(event) {
    if (_this.enabled === false) return;

    event.preventDefault();
    event.stopPropagation();

    _state = STATE.NONE;

    document.removeEventListener('mousemove', mousemove);
    document.removeEventListener('mouseup', mouseup);
    _this.dispatchEvent(endEvent);
  }

  function mousewheel(event) {
    if (_this.enabled === false) return;

    event.preventDefault();
    event.stopPropagation();

    switch (event.deltaMode) {
      case 2:
        // Zoom in pages
        _zoomStart.y -= event.deltaY * 0.025;
        break;

      case 1:
        // Zoom in lines
        _zoomStart.y -= event.deltaY * 0.01;
        break;

      default:
        // undefined, 0, assume pixels
        _zoomStart.y -= event.deltaY * 0.00025;
        break;
    }

    _this.dispatchEvent(startEvent);
    _this.dispatchEvent(endEvent);
  }

  function touchstart(event) {
    if (_this.enabled === false) return;

    switch (event.touches.length) {
      case 1:
        _state = STATE.TOUCH_ROTATE;
        _rotateStart.copy(
          getMouseProjectionOnBall(
            event.touches[0].pageX,
            event.touches[0].pageY,
          ),
        );
        _rotateEnd.copy(_rotateStart);
        break;

      case 2:
        _state = STATE.TOUCH_ZOOM_PAN;
        var dx = event.touches[0].pageX - event.touches[1].pageX;
        var dy = event.touches[0].pageY - event.touches[1].pageY;
        _touchZoomDistanceEnd = _touchZoomDistanceStart = Math.sqrt(
          dx * dx + dy * dy,
        );

        var x = (event.touches[0].pageX + event.touches[1].pageX) / 2;
        var y = (event.touches[0].pageY + event.touches[1].pageY) / 2;
        _panStart.copy(getMouseOnScreen(x, y));
        _panEnd.copy(_panStart);
        break;

      default:
        _state = STATE.NONE;
    }
    _this.dispatchEvent(startEvent);
  }

  function touchmove(event) {
    if (_this.enabled === false) return;

    event.preventDefault();
    event.stopPropagation();

    switch (event.touches.length) {
      case 1:
        _rotateEnd.copy(
          getMouseProjectionOnBall(
            event.touches[0].pageX,
            event.touches[0].pageY,
          ),
        );
        break;

      case 2:
        var dx = event.touches[0].pageX - event.touches[1].pageX;
        var dy = event.touches[0].pageY - event.touches[1].pageY;
        _touchZoomDistanceEnd = Math.sqrt(dx * dx + dy * dy);

        var x = (event.touches[0].pageX + event.touches[1].pageX) / 2;
        var y = (event.touches[0].pageY + event.touches[1].pageY) / 2;
        _panEnd.copy(getMouseOnScreen(x, y));
        break;

      default:
        _state = STATE.NONE;
    }

    //Paperless addition:
    //* This was added in PP-12220
    //* Note in renderer.ts we have `this.trackball.addEventListener('change', this.requestRenderIfNotRequested as () => void)`
    //  which is necessary because "Without this addition, the render loop continuity breaks and the orbit controls freeze up."
    //* Could have been added to help with debugging in the past?
    _this.dispatchEvent(changeEvent);
  }

  function touchend(event) {
    if (_this.enabled === false) return;

    switch (event.touches.length) {
      case 1:
        _rotateEnd.copy(
          getMouseProjectionOnBall(
            event.touches[0].pageX,
            event.touches[0].pageY,
          ),
        );
        _rotateStart.copy(_rotateEnd);
        break;

      case 2:
        _touchZoomDistanceStart = _touchZoomDistanceEnd = 0;

        var x = (event.touches[0].pageX + event.touches[1].pageX) / 2;
        var y = (event.touches[0].pageY + event.touches[1].pageY) / 2;
        _panEnd.copy(getMouseOnScreen(x, y));
        _panStart.copy(_panEnd);
        break;
    }

    _state = STATE.NONE;
    _this.dispatchEvent(endEvent);
  }

  function contextmenu(event) {
    event.preventDefault();
  }

  this.dispose = function () {
    this.domElement.removeEventListener('contextmenu', contextmenu, false);
    this.domElement.removeEventListener('mousedown', mousedown, false);
    this.domElement.removeEventListener('wheel', mousewheel, false);

    this.domElement.removeEventListener('touchstart', touchstart, false);
    this.domElement.removeEventListener('touchend', touchend, false);
    this.domElement.removeEventListener('touchmove', touchmove, false);

    document.removeEventListener('mousemove', mousemove, false);
    document.removeEventListener('mouseup', mouseup, false);

    window.removeEventListener('keydown', keydown, false);
    window.removeEventListener('keyup', keyup, false);
  };

  this.domElement.addEventListener('contextmenu', contextmenu, false);
  this.domElement.addEventListener('mousedown', mousedown, false);
  this.domElement.addEventListener('wheel', mousewheel, false);

  this.domElement.addEventListener('touchstart', touchstart, false);
  this.domElement.addEventListener('touchend', touchend, false);
  this.domElement.addEventListener('touchmove', touchmove, false);

  window.addEventListener('keydown', keydown, false);
  window.addEventListener('keyup', keyup, false);

  this.handleResize();
  //Paperless previously used a refactored this.handleResize() that was flawed... (see notes about handleResize elsewhere in this src)
  //this.handleResize(offsetLeft, offsetTop);

  // force an update at start
  this.update();
};

OrthographicTrackballControls.prototype = Object.create(
  EventDispatcher.prototype,
);
OrthographicTrackballControls.prototype.constructor =
  OrthographicTrackballControls;

// Notes:
// * Really we should stop using OrthographicTrackballControls because this is old code that was removed from threejs
//   See https://paperlessparts.atlassian.net/browse/PP-11220
// * THREE.OrthographicTrackballControls was removed in release r109. https://github.com/mrdoob/three.js/wiki/Migration-Guide#r108--r109
// * The last released implementation was:
//   * https://github.com/mrdoob/three.js/blob/7e0a78beb9317e580d7fa4da9b5b12be051c6feb/examples/js/controls/OrthographicTrackballControls.js
// * Paperless Parts was using a modified version of the original original `OrthographicTrackballControls.js`
// * Below is a summary of the changes from the original implementation.
//
//   > Side note: Darcy identified these on 3/3/2023 and removed some of the modifications when it was discovered some of the modifications
//   > caused bugs in digiquote. It is possible the bugs in digiquote were not always present and that they surfaced when the styling of the
//   > parent elements of the viewer's canvas changed.  Regardless, it is clear the previous refactored implementation was flawed for various edge cases...
//   > so some changes were revertd back to the original and this resolved the bugs in digiquote.)
//
//   * No functional changes:
//     * Rename of scale_x to scaleX, and scale_y to scaleY (eslint rule)
//     * in rotateCamera(): `var factor` was declared twice in same fn, which caused an eslint error. Hoisting the declaration fixed eslint issue
//       * angle = Math.min(angle, Math.PI / 4);
//     * Formatting by prettier
//   * Added an onZoomChange => assigned to this.onZoomChange,
//   * Minor functional change (or atleast appear to be minor):
//     * rotateCamera(): we added code to clamp `angle`: `angle = Math.min(angle, Math.PI / 4);`
//       * So we never rotate more than 45 degrees at a time
//     * zoomCamera(): calls `this.onZoomChange(_this.object.zoom);`
//     * mousemove() and touchmove: calls `_this.dispatchEvent(changeEvent);`
//       * the dispatched event is simply `changeEvent`
//   * Note, prior to 3/3/2023, we also used a refactored this.handleResize() that was flawed
//     * Received offsetLeft, offsetTop in constructor and this.handleResize() and had its own logic for calculating state
//       about screensize that was flawed for some edge cases.
//     * Reverted back to the original in PR for https://paperlessparts.atlassian.net/browse/PP-14322
//     * Now the changes from the original OrthographicTrackballControls implementation are minimal.
//       * essentially we added an onZoomChange() callback

export default OrthographicTrackballControls;
